/**
 * Utility functions for handling lot pricing across different data sources
 */

/**
 * Gets the final sale price for a lot, checking multiple data sources
 * @param lot - The lot object which may contain current_bid and/or lot_prices data
 * @returns The final sale price or null if no price is available
 */
export function getFinalSalePrice(lot: any): number | null {
  if (!lot) return null;
  
  // For sold or passed lots, check multiple sources for the final price
  if (lot.status === 'sold' || lot.status === 'passed') {
    // Priority order:
    // 1. current_bid (set by admin for newer auctions)
    // 2. lot_prices.price (for older auctions stored in separate table)  
    // 3. starting_bid as final fallback (if no other price data)
    
    if (lot.current_bid && lot.current_bid > 0) {
      return lot.current_bid;
    }
    
    if (lot.lot_prices?.price && lot.lot_prices.price > 0) {
      return lot.lot_prices.price;
    }
    
    // For sold lots without any price data, return starting bid as fallback
    // For passed lots, this represents the highest bid reached
    if (lot.starting_bid && lot.starting_bid > 0) {
      return lot.starting_bid;
    }
  }
  
  return null;
}

/**
 * Gets the current bid or starting bid for active lots
 * @param lot - The lot object
 * @returns The current bid amount
 */
export function getCurrentBidAmount(lot: any): number {
  if (!lot) return 0;
  
  // For active lots, show current bid or starting bid
  return lot.current_bid || lot.starting_bid || 0;
}

/**
 * Determines if a lot has price information available for display
 * @param lot - The lot object
 * @returns True if price information is available
 */
export function hasValidPriceInfo(lot: any): boolean {
  if (!lot) return false;
  
  const finalPrice = getFinalSalePrice(lot);
  return finalPrice !== null && finalPrice > 0;
} 